import { createContext, useContext, type ReactNode } from 'react'
import type { components } from '@mdhp/api-client/schemas/main-api'
import { mainApiClient } from './main-api-client'

type FeatureFlagName = components['schemas']['FeatureFlagName']

type BooleanFlagName = Extract<
  FeatureFlagName,
  'EMERGENCY_ESCALATION_ENABLED' | 'LABS_TXHB_BLOCKED' | 'SAVINGS_ENABLED'
>

type FeatureFlags = Record<BooleanFlagName, boolean> & { ENV: string | null }

const FeatureFlagsContext = createContext<FeatureFlags | undefined>(undefined)

const DEFAULT_FLAGS: FeatureFlags = {
  EMERGENCY_ESCALATION_ENABLED: false,
  LABS_TXHB_BLOCKED: true,
  SAVINGS_ENABLED: false,
  ENV: null,
}

function buildFlagsObject(flags: { name: FeatureFlagName; enabled: boolean; value?: string | null }[]): FeatureFlags {
  const result = { ...DEFAULT_FLAGS }

  for (const flag of flags) {
    if (flag.name === 'ENV') result.ENV = flag.value ?? null
    else if (flag.name in DEFAULT_FLAGS) result[flag.name as BooleanFlagName] = flag.enabled
  }

  return result
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { data } = mainApiClient.useQuery('get', '/utils/feature-flags', {}, { staleTime: 5 * 60 * 1000 })

  return <FeatureFlagsContext.Provider value={buildFlagsObject(data ?? [])}>{children}</FeatureFlagsContext.Provider>
}

export function useFeatureFlags(): FeatureFlags {
  const ctx = useContext(FeatureFlagsContext)

  if (ctx === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider')
  }

  return ctx
}
